import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import {
  FileOutput,
  Info,
  CheckCircle2,
  Braces,
  ScanSearch,
  Layers,
  Tags,
  PenLine,
  Sparkles,
  AlertTriangle,
} from 'lucide-react';

interface OATemplateGuideProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function OATemplateGuide({ open, onOpenChange }: OATemplateGuideProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!flex !flex-col max-w-[700px] max-h-[85vh] p-0 gap-0 overflow-hidden">
        <div className="flex-shrink-0 px-6 py-4 border-b bg-gradient-to-r from-red-50 to-orange-50">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2">
              <FileOutput className="w-5 h-5 text-red-600" />
              OA Response Templates Guide
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mt-1">
            Learn how to create, configure, and use Office Action response templates.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="px-6 py-5 space-y-6">

            {/* Overview */}
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-600" />
                Overview
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                OA Response Templates let you upload your firm's standard Office Action response
                shell document (.docx) and automatically generate a filled response for any
                application. Patent Loop identifies placeholders in your template, maps them to
                data from the selected Office Action and application, and produces a ready-to-review
                Word document with all formatting preserved.
              </p>
            </section>

            <Separator />

            {/* How Auto-Detect Works */}
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <ScanSearch className="w-4 h-4 text-teal-600" />
                Auto-Detect Fields
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                When you upload a template with the <strong>"Auto-detect placeholders"</strong> checkbox
                enabled, Patent Loop uses AI to scan your document and automatically identify:
              </p>
              <div className="space-y-2 ml-1">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Dynamic Fields</strong> — Variable data like serial numbers, dates, examiner
                    names, art unit numbers, attorney names, and other short values that change per application.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Sections</strong> — Larger content blocks such as claim amendments, remarks
                    addressing specific rejections, and other substantive portions of the response.
                  </p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                <p className="text-sm text-amber-800">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1 mb-0.5" />
                  Auto-detect works best when your shell document contains realistic placeholder text
                  (e.g., "John Doe" for attorney name, "XX/XXX,XXX" for serial number). If you prefer
                  full control, you can disable auto-detect and add fields manually.
                </p>
              </div>
            </section>

            <Separator />

            {/* Using Curly Braces */}
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Braces className="w-4 h-4 text-purple-600" />
                Using Placeholder Syntax
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Placeholders use double curly braces: <code className="px-1.5 py-0.5 bg-slate-100 rounded text-purple-700 font-mono text-xs">{'{{field_name}}'}</code>.
                You can include these directly in your Word document or let auto-detect create them for you.
              </p>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                <h4 className="text-sm font-semibold text-gray-800">Dynamic Field Examples</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  <div className="font-mono bg-white px-2 py-1 rounded border border-slate-200 text-teal-700">{'{{application_number}}'}</div>
                  <div className="text-gray-600 py-1">Serial number (e.g., 16/123,456)</div>
                  <div className="font-mono bg-white px-2 py-1 rounded border border-slate-200 text-teal-700">{'{{filing_date}}'}</div>
                  <div className="text-gray-600 py-1">Application filing date</div>
                  <div className="font-mono bg-white px-2 py-1 rounded border border-slate-200 text-teal-700">{'{{examiner_name}}'}</div>
                  <div className="text-gray-600 py-1">Primary examiner's name</div>
                  <div className="font-mono bg-white px-2 py-1 rounded border border-slate-200 text-teal-700">{'{{art_unit}}'}</div>
                  <div className="text-gray-600 py-1">Art unit number</div>
                  <div className="font-mono bg-white px-2 py-1 rounded border border-slate-200 text-teal-700">{'{{attorney_name}}'}</div>
                  <div className="text-gray-600 py-1">Attorney/agent name</div>
                  <div className="font-mono bg-white px-2 py-1 rounded border border-slate-200 text-teal-700">{'{{oa_date}}'}</div>
                  <div className="text-gray-600 py-1">Office Action mailing date</div>
                  <div className="font-mono bg-white px-2 py-1 rounded border border-slate-200 text-teal-700">{'{{current_date}}'}</div>
                  <div className="text-gray-600 py-1">Today's date (auto-updating Word field)</div>
                  <div className="font-mono bg-white px-2 py-1 rounded border border-slate-200 text-teal-700">{'{{confirmation_number}}'}</div>
                  <div className="text-gray-600 py-1">Application confirmation number</div>
                </div>

                <Separator className="my-2" />

                <h4 className="text-sm font-semibold text-gray-800">Section Examples</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  <div className="font-mono bg-white px-2 py-1 rounded border border-slate-200 text-purple-700">{'{{claims_section}}'}</div>
                  <div className="text-gray-600 py-1">Amended claims with status indicators</div>
                  <div className="font-mono bg-white px-2 py-1 rounded border border-slate-200 text-purple-700">{'{{remarks_section_1}}'}</div>
                  <div className="text-gray-600 py-1">Remarks for first rejection type</div>
                  <div className="font-mono bg-white px-2 py-1 rounded border border-slate-200 text-purple-700">{'{{remarks_section_2}}'}</div>
                  <div className="text-gray-600 py-1">Remarks for second rejection type</div>
                  <div className="font-mono bg-white px-2 py-1 rounded border border-slate-200 text-purple-700">{'{{objections_section}}'}</div>
                  <div className="text-gray-600 py-1">Response to claim objections</div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                <p className="text-sm text-blue-800">
                  <Info className="w-3.5 h-3.5 inline mr-1 mb-0.5" />
                  You can name placeholders however you like — the names are descriptive labels that
                  help the AI understand what content belongs in each location. The key is using
                  the <code className="font-mono text-xs">{'{{ }}'}</code> syntax consistently.
                </p>
              </div>
            </section>

            <Separator />

            {/* Dynamic Fields vs Sections */}
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Layers className="w-4 h-4 text-orange-600" />
                Dynamic Fields vs. Sections
              </h3>
              <div className="space-y-3">
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                  <h4 className="font-medium text-gray-900 mb-1 text-sm">Dynamic Fields</h4>
                  <p className="text-sm text-gray-700">
                    Short, single-value replacements — a field name, date, number, or brief phrase.
                    These are replaced inline exactly where the placeholder appears. Application
                    numbers are automatically formatted as XX/XXX,XXX, dates are formatted formally
                    (e.g., "February 11, 2026"), and <code className="font-mono text-xs">{'{{current_date}}'}</code> becomes
                    a Word auto-updating date field.
                  </p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <h4 className="font-medium text-gray-900 mb-1 text-sm">Sections</h4>
                  <p className="text-sm text-gray-700">
                    Longer content blocks — full paragraphs of remarks, arguments, or claim text.
                    The placeholder is replaced with substantive content generated from the Office
                    Action analysis. If a section's content is empty (e.g., no relevant rejection of
                    that type), the placeholder paragraph and its preceding header are automatically
                    removed to keep the document clean.
                  </p>
                </div>
              </div>
            </section>

            <Separator />

            {/* Editing Fields */}
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <PenLine className="w-4 h-4 text-sky-600" />
                Editing Template Fields
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                After uploading a template, click <strong>"Show Fields"</strong> in the template
                viewer toolbar to see and edit the detected fields. You can:
              </p>
              <div className="space-y-2 ml-1">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Edit field names</strong> — Change the placeholder key (the part inside
                    the curly braces) to better describe what should go there.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Edit descriptions</strong> — Update the description to guide the AI on
                    what content to generate for each field.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Add new fields</strong> — Click "Add Field" or "Add Section" to create
                    new placeholders. Make sure to also add the matching <code className="font-mono text-xs">{'{{placeholder}}'}</code> in
                    the document body where you want the content inserted.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Remove fields</strong> — Click the X button to remove a field that
                    isn't needed. The placeholder in the document will remain as-is if not removed
                    from the document body.
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-3">
                Click <strong>"Save Fields"</strong> to persist your changes. The field mapping is
                what the AI uses as a guide when generating filled responses.
              </p>
            </section>

            <Separator />

            {/* Response Types */}
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Tags className="w-4 h-4 text-indigo-600" />
                Response Types
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Each template can be assigned a <strong>Response Type</strong> to categorize it and
                help the AI tailor the generated content. Select a type from the dropdown below the
                toolbar when viewing a template. Available types:
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-slate-50 rounded px-3 py-1.5 border border-slate-200">Non-Final OA Response</div>
                <div className="bg-slate-50 rounded px-3 py-1.5 border border-slate-200">Final OA Response</div>
                <div className="bg-slate-50 rounded px-3 py-1.5 border border-slate-200">Restriction/Election Response</div>
                <div className="bg-slate-50 rounded px-3 py-1.5 border border-slate-200">Preliminary Amendment</div>
                <div className="bg-slate-50 rounded px-3 py-1.5 border border-slate-200">General Transmittal</div>
                <div className="bg-slate-50 rounded px-3 py-1.5 border border-slate-200">Response to Ex Parte Quayle</div>
                <div className="bg-slate-50 rounded px-3 py-1.5 border border-slate-200">Comments on Notice of Allowance</div>
                <div className="bg-slate-50 rounded px-3 py-1.5 border border-slate-200 col-span-2">Response to Notice of Correct Application Papers</div>
              </div>
              <p className="text-sm text-gray-600 mt-3">
                Setting a response type helps the AI understand the context — for example, a Final
                OA Response will emphasize urgency and address all outstanding rejections, while a
                Restriction/Election Response will focus on election of species or groups. You can
                also filter templates by type in the left panel.
              </p>
            </section>

            <Separator />

            {/* Generation Workflow */}
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                Generating a Filled Response
              </h3>
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">1. Select a template</h4>
                  <p className="text-sm text-gray-700">
                    Click a template in the left panel. You can preview it in the center viewer and
                    review its fields.
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">2. Choose application and documents</h4>
                  <p className="text-sm text-gray-700">
                    In the right panel, enter an application number. Select the Office Action you're
                    responding to, and optionally a claims document (from prosecution history or
                    uploaded separately). You can also attach supplemental documents for additional context.
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">3. Generate</h4>
                  <p className="text-sm text-gray-700">
                    Click <strong>Generate</strong>. Patent Loop extracts issues from the Office
                    Action, maps them to your template fields, and produces a filled Word document.
                    The generated file appears in the filled templates list for download.
                  </p>
                </div>
              </div>
            </section>

            <Separator />

            {/* Tips & Best Practices */}
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-3">Tips & Best Practices</h3>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Use your existing shell document</strong> — Upload the same Word file you already
                    use as a starting point. No special formatting required.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Include placeholders in the document</strong> — For best results, put
                    the <code className="font-mono text-xs">{'{{field_name}}'}</code> placeholders directly in
                    your Word document where you want content inserted, then match them in the fields panel.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Upload claims separately</strong> — For best claim amendment formatting,
                    upload the latest claim set or select it from prosecution history.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Set a response type</strong> — Assigning a response type helps the AI
                    generate more contextually appropriate content for each template.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Review and edit the output</strong> — The generated document is a first
                    draft. Review the filled content, refine the arguments, and finalize before filing.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Reuse templates</strong> — Templates are saved to your account and can
                    be used across multiple applications and Office Actions.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Formatting is preserved</strong> — Fonts, headers, footers, signature
                    blocks, bold/italic text, and paragraph spacing are all maintained in the
                    generated document.
                  </p>
                </div>
              </div>
            </section>

          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
